import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useGastosContext } from '../../contexts/GastosContext'
import { useMetasContext } from '../../contexts/MetasContext'
import { useTheme } from '../../contexts/ThemeContext'
import { STORAGE_KEYS } from '../../lib/storage'
import { fonts } from '../../lib/themes'
import {
  IcSeed as IcNecesario, IcSparkAlt as IcAlegro, IcWind, IcCloud,
} from '../ui/Icons'
import { CATEGORIAS_GASTO, CATEGORIAS_INGRESO } from '../../lib/categorias'

const EMOCIONES = [
  { icon: IcNecesario, label: 'Necesario',  desc: 'lo necesitaba',   color: '#8EAA7A' },
  { icon: IcAlegro,    label: 'Me alegró',  desc: 'valió cada peso', color: '#C8A04A' },
  { icon: IcWind,      label: 'Impulsivo',  desc: 'lo reconozco',    color: '#C89898' },
  { icon: IcCloud,     label: 'Obligación', desc: 'tocaba pagarlo',  color: '#A89078' },
]

export default function Registro() {
  const navigate = useNavigate()
  const location = useLocation()
  const { gastos, agregarGasto } = useGastosContext()
  const { metas, aportarAMeta, gastarDeMeta, editarStatus, fetchCategorias, gastarEnCategoria } = useMetasContext()
  const { theme } = useTheme()
  const s = makeStyles(theme)

  const lastMonto = localStorage.getItem(STORAGE_KEYS.LAST_MONTO)
  const locationGastandoId = location.state?.gastandoMetaId || null

  const [monto,        setMonto]        = useState('0')
  const [tipo,         setTipo]         = useState('gasto')
  const [categoria,    setCategoria]    = useState(null)
  const [emocion,      setEmocion]      = useState(null)
  const [metaId,       setMetaId]       = useState(null)
  const [metaGastoId,  setMetaGastoId]  = useState(locationGastandoId)
  const [nota,         setNota]         = useState('')
  const [fecha,        setFecha]        = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  })
  const [guardando,    setGuardando]    = useState(false)
  const [modalCiclo,   setModalCiclo]   = useState(false)
  const [metaVaciada,  setMetaVaciada]  = useState(null)
  const [metaCatId,    setMetaCatId]    = useState(null)
  const [metaCats,     setMetaCats]     = useState([])

  const gastandoMetas = metas.filter(m => m.status === 'en_progreso' && m.fase === 'gastando')

  useEffect(() => {
    setMetaCatId(null)
    if (!metaGastoId) { setMetaCats([]); return }
    const m = gastandoMetas.find(m => m.id === metaGastoId)
    if (!m || (m.tipo !== 'viaje' && m.tipo !== 'proyecto')) { setMetaCats([]); return }
    fetchCategorias(metaGastoId).then(({ data }) => setMetaCats(data || []))
  }, [metaGastoId]) // eslint-disable-line

  const sugeridas = useMemo(() => {
    if (tipo === 'ahorro') return []
    const pool = tipo === 'ingreso' ? CATEGORIAS_INGRESO : CATEGORIAS_GASTO
    const filtrados = gastos.filter(g => g.tipo === tipo)
    const freq = {}
    filtrados.slice(0, 15).forEach(g => { freq[g.categoria] = (freq[g.categoria] || 0) + 1 })
    return [...new Set(Object.entries(freq).sort((a,b) => b[1]-a[1]).map(e => e[0]))].slice(0, 3)
      .map(label => pool.find(c => c.label === label)).filter(Boolean)
  }, [gastos, tipo])

  function handleTipoChange(t) {
    setTipo(t)
    setCategoria(null)
    setEmocion(null)
    if (t !== 'ahorro') setMetaId(null)
    if (t !== 'gasto') { setMetaGastoId(null); setMetaCatId(null); setMetaCats([]) }
  }

  const montoNum = parseFloat(monto) || 0
  const montoColor = tipo === 'ingreso' ? theme.sage : tipo === 'ahorro' ? theme.gold : theme.terra

  const haMetaCats = tipo === 'gasto' && !!metaGastoId && metaCats.length > 0

  const canSave =
    tipo === 'ahorro'  ? montoNum > 0 :
    tipo === 'ingreso' ? montoNum > 0 && categoria :
    /* gasto */          montoNum > 0 && emocion && (haMetaCats || !!categoria)

  async function guardar() {
    if (!canSave || guardando) return
    setGuardando(true)
    localStorage.setItem(STORAGE_KEYS.LAST_MONTO, monto)

    const metaSeleccionada = metas.find(m => m.id === metaId)
    const metaCatSel = metaCats.find(c => c.id === metaCatId)
    const descripcion = tipo === 'ahorro'
      ? (metaSeleccionada ? `Ahorro → ${metaSeleccionada.nombre}` : 'Ahorro general')
      : (nota || (metaCatSel ? `${metaCatSel.emoji || ''} ${metaCatSel.nombre}`.trim() : categoria?.label))

    try {
      const { error: gastoError } = await agregarGasto({
        monto:       montoNum,
        tipo,
        categoria:   tipo === 'ahorro' ? 'Ahorro' : haMetaCats ? (metaCatSel?.nombre || gastandoMetas.find(m => m.id === metaGastoId)?.nombre || 'Meta') : categoria.label,
        emoji:       tipo === 'ahorro' ? (metaSeleccionada?.emoji || '🎯') : haMetaCats ? (metaCatSel?.emoji || gastandoMetas.find(m => m.id === metaGastoId)?.emoji || '💸') : categoria.emoji,
        emocion:     tipo === 'ahorro' ? 'Meta' : tipo === 'ingreso' ? null : emocion.label,
        descripcion,
        nota:        nota || null,
        meta_id: (tipo === 'ahorro' && metaId) ? metaId : (tipo === 'gasto' && metaGastoId) ? metaGastoId : null,
        ...(tipo === 'gasto' && metaCatId ? { meta_categoria_id: metaCatId } : {}),
        fecha:       new Date(fecha + 'T12:00:00').toISOString(),
      })

      if (gastoError) return

      // si hay meta seleccionada, también actualizar su acumulado
      if (tipo === 'ahorro' && metaId) {
        await aportarAMeta(metaId, montoNum)
        const metaAhorro = metas.find(m => m.id === metaId)
        if (metaAhorro && metaAhorro.objetivo > 0 && (metaAhorro.acumulado || 0) + montoNum >= metaAhorro.objetivo) {
          navigate('/metas', { state: { completadaMetaId: metaId } })
          return
        }
      }

      // si el gasto va a cuenta de una meta en modo gastando, registrar
      if (tipo === 'gasto' && metaGastoId) {
        const { error: gastarErr, disponible } = await gastarDeMeta(metaGastoId, montoNum)
        if (metaCatId && !gastarErr) await gastarEnCategoria(metaCatId, montoNum)
        if (!gastarErr && disponible === 0) {
          const mv = metas.find(m => m.id === metaGastoId)
          setMetaVaciada(mv || null)
          setModalCiclo(true)
          setGuardando(false)
          return
        }
      }

      navigate('/')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div style={s.container}>
      {/* Top bar */}
      <div style={s.topbar}>
        <button style={s.backBtn} onClick={() => navigate('/')}>←</button>
        <h1 style={s.topTitle}>
          Registrar <em style={{ color: theme.accent }}>con intención</em>
        </h1>
      </div>

      {/* Tipo */}
      <div style={s.tipoRow}>
        {['gasto','ingreso','ahorro'].map(t => (
          <button key={t} onClick={() => handleTipoChange(t)}
            style={{ ...s.tipoBtn, ...(tipo === t ? { borderColor: montoColor, color: montoColor, background: montoColor + '18', fontWeight: 600 } : {}) }}>
            {{ gasto:'gasto', ingreso:'ingreso', ahorro:'aporte' }[t]}
          </button>
        ))}
      </div>

      {/* Monto */}
      <div style={s.montoRow}>
        <span style={{ ...s.montoDisplay, color: montoColor }}>$</span>
        <input
          type="text"
          inputMode="decimal"
          autoFocus
          style={{ ...s.montoDisplay, color: montoColor, background: 'transparent', border: 'none', outline: 'none', padding: 0, minWidth: 40, width: `${Math.max(monto.length, 1)}ch` }}
          value={monto === '0' ? '' : monto}
          placeholder="0"
          onChange={e => {
            const val = e.target.value.replace(/[^0-9.]/g, '')
            if ((val.match(/\./g) || []).length > 1) return
            setMonto(val || '0')
          }}
        />
      </div>


      <div style={s.divider} />

      {/* ── FLUJO AHORRO ── */}
      {tipo === 'ahorro' && (
        <div style={s.section}>
          <p style={s.sectionLabel}>¿hacia dónde va este ahorro?</p>
          <div style={s.metaList}>
            {/* Opción general */}
            <button
              style={{ ...s.metaOption, ...(metaId === null ? { borderColor: theme.gold, background: theme.gold + '18' } : { borderColor: theme.cardBorder }) }}
              onClick={() => setMetaId(null)}
            >
              <span style={s.metaOptionIcon}>🏦</span>
              <div style={s.metaOptionInfo}>
                <span style={{ ...s.metaOptionName, color: theme.ink }}>Ahorro general</span>
                <span style={{ ...s.metaOptionSub, color: theme.inkLight }}>sin meta específica</span>
              </div>
              {metaId === null && <span style={{ color: theme.gold, fontSize: 16 }}>✓</span>}
            </button>
            {/* Metas existentes */}
            {metas.filter(m => m.status === 'en_progreso' || !m.status).map(meta => {
              const pct = meta.objetivo > 0 ? Math.round((meta.acumulado / meta.objetivo) * 100) : 0
              const active = metaId === meta.id
              return (
                <button
                  key={meta.id}
                  style={{ ...s.metaOption, ...(active ? { borderColor: theme.gold, background: theme.gold + '18' } : { borderColor: theme.cardBorder }) }}
                  onClick={() => setMetaId(meta.id)}
                >
                  <span style={s.metaOptionIcon}>{meta.emoji}</span>
                  <div style={s.metaOptionInfo}>
                    <span style={{ ...s.metaOptionName, color: theme.ink }}>{meta.nombre}</span>
                    <span style={{ ...s.metaOptionSub, color: theme.inkLight }}>{pct}% completada</span>
                  </div>
                  {active && <span style={{ color: theme.gold, fontSize: 16 }}>✓</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* ── FLUJO GASTO ── */}
      {tipo === 'gasto' && (
        <>
          {/* 1. Meta picker — primero */}
          {gastandoMetas.length > 0 && (
            <div style={s.section}>
              <p style={s.sectionLabel}>¿va a cuenta de una meta?</p>
              <div style={s.metaList}>
                <button
                  style={{ ...s.metaOption, ...(metaGastoId === null ? { borderColor: theme.lav, background: theme.lav + '18' } : { borderColor: theme.cardBorder }) }}
                  onClick={() => setMetaGastoId(null)}
                >
                  <span style={s.metaOptionIcon}>💳</span>
                  <div style={s.metaOptionInfo}>
                    <span style={{ ...s.metaOptionName, color: theme.ink }}>no, gasto independiente</span>
                    <span style={{ ...s.metaOptionSub, color: theme.inkLight }}>no se descuenta de ninguna meta</span>
                  </div>
                  {metaGastoId === null && <span style={{ color: theme.lav, fontSize: 16 }}>✓</span>}
                </button>
                {gastandoMetas.map(m => {
                  const active = metaGastoId === m.id
                  const disp = Math.max(0, (m.acumulado || 0) - (m.gastado_meta || 0))
                  return (
                    <button key={m.id}
                      style={{ ...s.metaOption, ...(active ? { borderColor: theme.lav, background: theme.lav + '18' } : { borderColor: theme.cardBorder }) }}
                      onClick={() => setMetaGastoId(m.id)}
                    >
                      <span style={s.metaOptionIcon}>{m.emoji}</span>
                      <div style={s.metaOptionInfo}>
                        <span style={{ ...s.metaOptionName, color: theme.ink }}>{m.nombre}</span>
                        <span style={{ ...s.metaOptionSub, color: theme.inkLight }}>${disp.toLocaleString('es')} disponibles</span>
                      </div>
                      {active && <span style={{ color: theme.lav, fontSize: 16 }}>✓</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* 2. Categoría: sub-cats de meta OR picker regular */}
          {haMetaCats ? (
            <div style={s.section}>
              <p style={s.sectionLabel}>¿en qué categoría va?</p>
              <div style={s.catGrid}>
                {metaCats.map(cat => {
                  const active = metaCatId === cat.id
                  return (
                    <button key={cat.id} onClick={() => setMetaCatId(active ? null : cat.id)}
                      style={{ ...s.catItem, ...(active ? { borderColor: theme.lav, background: theme.lav + '18', color: theme.lav } : { borderColor: theme.cardBorder, color: theme.inkLight }) }}>
                      <span style={{ fontSize: 20 }}>{cat.emoji}</span>
                      <span style={{ ...s.catLabel, color: active ? theme.lav : theme.inkMid }}>{cat.nombre}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ) : (
            <>
              <div style={s.section}>
                <p style={s.sectionLabel}>¿En qué?</p>
                <div style={s.catGrid}>
                  {CATEGORIAS_GASTO.map(c => {
                    const active = categoria?.label === c.label
                    const Ic = c.icon
                    return (
                      <button key={c.label} onClick={() => setCategoria(c)}
                        style={{ ...s.catItem, ...(active ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : { borderColor: theme.cardBorder, color: theme.inkLight }) }}>
                        {Ic && <Ic size={20} />}
                        <span style={{ ...s.catLabel, color: active ? theme.accent : theme.inkMid }}>{c.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </>
          )}

          {/* 3. Emoción */}
          <div style={s.section}>
            <p style={s.sectionLabel}>¿Cómo lo sentiste?</p>
            <div style={s.emoGrid}>
              {EMOCIONES.map(e => {
                const active = emocion?.label === e.label
                const Ic = e.icon
                return (
                  <button key={e.label} onClick={() => setEmocion(e)}
                    style={{ ...s.emoItem, color: e.color, ...(active ? { borderColor: e.color, background: e.color + '20' } : { borderColor: theme.cardBorder }) }}>
                    {Ic && <Ic size={18} style={{ display: 'block', margin: '0 auto 3px' }} />}
                    <span style={{ ...s.emoLabel, color: active ? e.color : theme.ink }}>{e.label}</span>
                    <span style={{ ...s.emoDesc, color: theme.inkLight }}>{e.desc}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* ── FLUJO INGRESO ── */}
      {tipo === 'ingreso' && (
        <>
          <div style={s.section}>
            <p style={s.sectionLabel}>¿De dónde viene?</p>
            <div style={s.catGrid}>
              {CATEGORIAS_INGRESO.map(c => {
                const active = categoria?.label === c.label
                const Ic = c.icon
                return (
                  <button key={c.label} onClick={() => setCategoria(c)}
                    style={{ ...s.catItem, ...(active ? { borderColor: theme.accent, background: theme.accent + '18', color: theme.accent } : { borderColor: theme.cardBorder, color: theme.inkLight }) }}>
                    {Ic && <Ic size={20} />}
                    <span style={{ ...s.catLabel, color: active ? theme.accent : theme.inkMid }}>{c.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Nota */}
      <div style={s.section}>
        <p style={s.sectionLabel}>Una nota (opcional)</p>
        <textarea
          style={{ ...s.nota, background: theme.inputBg, borderColor: theme.cardBorder, color: theme.ink }}
          rows={3}
          placeholder={tipo === 'ahorro' ? 'ej: para el viaje de verano, el colchón de emergencias...' : 'ej: almuerzo con mamá, el café que necesitaba...'}
          value={nota}
          onChange={e => setNota(e.target.value)}
        />
      </div>

      {/* Fecha */}
      <div style={s.section}>
        <p style={s.sectionLabel}>{{ gasto: 'fecha del gasto', ingreso: 'fecha del ingreso', ahorro: 'fecha del ahorro' }[tipo]}</p>
        <input
          type="date"
          style={{ ...s.fechaInput, background: theme.inputBg, borderColor: theme.cardBorder, color: theme.ink }}
          value={fecha}
          max={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` })()}
          onChange={e => setFecha(e.target.value)}
        />
      </div>

      <div style={{ height: 160 }} />

      {/* Botón sticky */}
      <div style={s.stickyBar}>
        <button
          style={{ ...s.submitBtn, opacity: canSave ? 1 : 0.5, background: theme.cardGradient }}
          onClick={guardar}
          disabled={!canSave || guardando}
        >
          {guardando ? 'guardando...' : 'Registrar con intención ✦'}
        </button>
      </div>

      {/* Modal ciclo completado */}
      {modalCiclo && metaVaciada && (
        <div style={s.modalOverlay}>
          <div style={s.modalSheet}>
            <p style={s.modalEmoji}>{metaVaciada.emoji}</p>
            <h2 style={s.modalTitle}>ciclo completado ✦</h2>
            <p style={s.modalBody}>
              gastaste todo lo que habías guardado para <em>{metaVaciada.nombre}</em>.
              ese dinero cumplió su propósito. bien hecho.
            </p>
            <button
              style={s.modalPrimaryBtn}
              onClick={async () => {
                await editarStatus(metaVaciada.id, 'terminada')
                navigate('/')
              }}
            >
              continuar ✦
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const makeStyles = (t) => ({
  container: {
    maxWidth: 560, margin: '0 auto', padding: '24px 0 0',
    fontFamily: "'DM Sans',sans-serif", background: t.cream, minHeight: '100vh',
  },
  topbar: { display: 'flex', alignItems: 'center', gap: 14, padding: '8px 20px 16px' },
  backBtn: {
    width: 38, height: 38, borderRadius: 12, background: t.paper,
    border: `1.5px solid ${t.cardBorder}`, fontSize: 18, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    color: t.ink,
  },
  topTitle: { fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 400, color: t.ink },
  tipoRow: { display: 'flex', gap: 10, padding: '0 20px', marginBottom: 20, justifyContent: 'center' },
  tipoBtn: {
    padding: '8px 20px', borderRadius: 20, border: `1.5px solid ${t.cardBorder}`,
    background: t.paper, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    color: t.inkLight, transition: 'all 0.2s',
  },
  montoRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, padding: '0 20px 4px', lineHeight: 1 },
  montoDisplay: {
    fontFamily: "'Cormorant Garamond',serif", fontSize: 68, fontWeight: 300,
    letterSpacing: -2, lineHeight: 1,
  },
  lastMontoRow: { display: 'flex', justifyContent: 'center', marginBottom: 8 },
  lastMontoBtn: {
    padding: '6px 16px', borderRadius: 20, border: `1px solid ${t.cardBorder}`,
    background: t.paper, fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14,
    color: t.inkLight, cursor: 'pointer',
  },
  divider: { height: 1, background: t.cardBorder, margin: '4px 20px 18px' },
  section: { padding: '0 20px', marginBottom: 22 },
  sectionLabel: {
    fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase',
    color: t.inkLight, fontWeight: 500, marginBottom: 10,
  },
  metaList: { display: 'flex', flexDirection: 'column', gap: 10 },
  metaOption: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '13px 14px', borderRadius: 16,
    border: `1.5px solid ${t.cardBorder}`, background: t.paper,
    cursor: 'pointer', transition: 'all 0.2s', width: '100%', textAlign: 'left',
  },
  metaOptionIcon: { fontSize: 22, flexShrink: 0 },
  metaOptionInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  metaOptionName: { fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500 },
  metaOptionSub: { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13 },
  sugeridaRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  sugeridaBtn: {
    padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${t.cardBorder}`,
    background: t.paper, fontSize: 13, color: t.inkMid, cursor: 'pointer', transition: 'all 0.2s',
  },
  catGrid: { display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6 },
  catItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '10px 4px 8px', borderRadius: 12, border: `1.5px solid ${t.cardBorder}`,
    background: t.paper, cursor: 'pointer', transition: 'all 0.2s',
  },
  catLabel: { fontSize: 12, fontWeight: 500, lineHeight: 1.1, textAlign: 'center', color: t.inkMid },
  emoGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 },
  emoItem: {
    padding: '10px 4px', borderRadius: 12, border: `1.5px solid ${t.cardBorder}`,
    background: t.paper, cursor: 'pointer', transition: 'all 0.2s',
    textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 3,
  },
  emoLabel: { fontSize: 12, fontWeight: 500, lineHeight: 1.1 },
  emoDesc: { fontFamily: "'DM Sans', sans-serif", fontSize: 12, lineHeight: 1.2, color: t.inkLight },
  nota: {
    width: '100%', border: `1.5px solid`, borderRadius: 16,
    padding: '13px 15px', fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 16,
    outline: 'none', resize: 'none', lineHeight: 1.6, boxSizing: 'border-box',
  },
  fechaInput: {
    width: '100%', border: `1.5px solid`, borderRadius: 14,
    padding: '11px 14px', fontFamily: "'DM Sans',sans-serif", fontSize: 15,
    outline: 'none', boxSizing: 'border-box',
  },
  stickyBar: {
    position: 'fixed', bottom: 76, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 560, padding: '0 20px', boxSizing: 'border-box', zIndex: 150,
  },
  submitBtn: {
    width: '100%', padding: 18, border: 'none', borderRadius: 22,
    color: 'white', fontFamily: "'Cormorant Garamond',serif",
    fontSize: 22, fontStyle: 'italic', cursor: 'pointer',
    boxShadow: `0 10px 28px ${t.accentDeep}44`, transition: 'all 0.2s',
  },
  modalOverlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 500, display: 'flex', alignItems: 'flex-end' },
  modalSheet:      { width: '100%', maxWidth: 560, margin: '0 auto', background: t.cream, borderRadius: '28px 28px 0 0', padding: '32px 28px 48px', textAlign: 'center' },
  modalEmoji:      { fontSize: 52, margin: '0 0 8px' },
  modalTitle:      { fontFamily: fonts.display, fontSize: 32, fontWeight: 300, fontStyle: 'italic', color: t.ink, margin: '0 0 14px' },
  modalBody:       { fontFamily: fonts.script, fontStyle: 'italic', fontSize: 16, color: t.inkMid, lineHeight: 1.6, margin: '0 0 28px' },
  modalPrimaryBtn: { width: '100%', padding: 18, border: 'none', borderRadius: 22, background: t.cardGradient, color: 'white', fontFamily: fonts.display, fontSize: 22, fontStyle: 'italic', cursor: 'pointer' },
})
